import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import theme from 'src/utils/styles/theme'
import { useStaticQuery, graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next'
import moment from 'moment-timezone'
import {
  VIP_CODE,
  BUY_LINK,
  CAL_BUY_LINK,
  FRAME_2_BUY_LINK,
  LOCALIZATION,
  SEASONAL_STATES,
} from 'src/utils/constants'
import {
  CountdownSection,
  CountdownSectionContainer,
  CountDownContainer,
  Header,
  SubHeader,
  Time,
  ColonWrapper,
  Label,
  BuyNowContainer,
  BuyNowLink,
  BuyNowText,
  Col,
  TextContainer,
  HideOnMobile,
  HideOnDesktop,
  TimeContainer,
} from './styles'

const SeasonalCountDownTimer = ({
  holidayDate,
  onCountdownComplete,
  holidayState,
  marginTop,
  frame,
  frame2,
}) => {
  const { bfcmVipBkgd, bfcmVipBkgdMobile } = useStaticQuery(graphql`
    query SeasonalCountDownTimerQuery {
      bfcmVipBkgd: file(relativePath: { eq: "md2026/countdown_bkgd_vip_desk.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      bfcmVipBkgdMobile: file(relativePath: { eq: "md2026/countdown_bkgd_vip_mobile.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
    }
  `)

  const { t } = useTranslation('common')

  const [showDays, setShowDays] = useState('00')
  const [showHours, setShowHours] = useState('00')
  const [showMinutes, setShowMinutes] = useState('00')
  const [showSeconds, setShowSeconds] = useState('00')
  const [countdownCompleted, setCountdownCompleted] = useState(false)

  const convertToDoubleDigitString = (integer) => {
    if (integer > 9) return integer.toString()
    if (integer < 0) return `00`
    return `0${integer}`
  }

  const timeZones = {
    en: 'America/Los_Angeles', // PT
    // 'en-ca': 'America/New_York', // ET
    'en-ca': 'America/Los_Angeles', // PT
    'en-au': 'Australia/Sydney', // AEDT
    'en-gb': 'Europe/London', // BST
  }

  const targetTimeZone = timeZones[LOCALIZATION] || 'America/Los_Angeles' // Get the target timezone based on localization
  const countDownDate = moment.tz(holidayDate, targetTimeZone).valueOf() // Convert holiday date to target timezone

  useEffect(() => {
    const now = moment().valueOf()

    if (countDownDate - now <= 0) {
      setCountdownCompleted(true)
      onCountdownComplete()
    }
  }, [countDownDate, onCountdownComplete])

  useEffect(() => {
    const x = setInterval(() => {
      const now = moment().valueOf()
      const distance = countDownDate - now
      if (distance <= 0) {
        clearInterval(x)
        setCountdownCompleted(true)
        setShowDays('00')
        setShowHours('00')
        setShowMinutes('00')
        setShowSeconds('00')

        if (onCountdownComplete) {
          onCountdownComplete() // Notify that the countdown is complete
        }
      } else {
        const days = convertToDoubleDigitString(Math.floor(distance / (1000 * 60 * 60 * 24)))
        const hours = convertToDoubleDigitString(
          Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        )
        const minutes = convertToDoubleDigitString(
          Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
        )
        const seconds = convertToDoubleDigitString(Math.floor((distance % (1000 * 60)) / 1000))
        setShowDays(days)
        setShowHours(hours)
        setShowMinutes(minutes)
        setShowSeconds(seconds)
      }
    }, 1000)

    return () => clearInterval(x)
  }, [countDownDate, onCountdownComplete])

  if (countdownCompleted) {
    return null
  }

  const timeDisplay = [
    { content: showDays, label: 'Days' },
    { content: showHours, label: 'Hrs' },
    { content: showMinutes, label: 'Mins' },
    { content: showSeconds, label: 'Secs' },
  ]
  if (countdownCompleted) {
    return null
  }

  const { bfcmCore, vip, bfcmLastChance, bau } = SEASONAL_STATES
  const holidayStates = {
    [bfcmCore]: {
      headerText: "The Mother's Day Sale Is On!",
      headerColor: theme.space,
      subHeaderText: "Save on Mom's most-wanted gifts.",
      subHeaderColor: theme.space,
      ctaText: 'Buy Now',
      ctaTextColor: theme.white,
      ctaTextColorMobile: theme.white,
      ctaBgColor: theme.coral,
      ctaLink: frame2 ? FRAME_2_BUY_LINK : frame ? BUY_LINK : CAL_BUY_LINK,
      labelColor: theme.space,
      timeColor: theme.space,
      bckgndDesk: bfcmVipBkgd.childImageSharp.gatsbyImageData,
      bckgndMobile: bfcmVipBkgdMobile.childImageSharp.gatsbyImageData,
    },
    [vip]: {
      headerText: 'VIP Early Access Is On!',
      headerColor: theme.space,
      subHeaderText: `Use code <b>${VIP_CODE}</b> to be the first to shop our Mother's Day deals.`,
      subHeaderColor: theme.space,
      ctaText: 'Buy Now',
      ctaTextColor: theme.white,
      ctaTextColorMobile: theme.white,
      ctaBgColor: theme.coral,
      ctaLink: frame2 ? FRAME_2_BUY_LINK : frame ? BUY_LINK : CAL_BUY_LINK,
      labelColor: theme.space,
      timeColor: theme.space,
      bckgndDesk: bfcmVipBkgd.childImageSharp.gatsbyImageData,
      bckgndMobile: bfcmVipBkgdMobile.childImageSharp.gatsbyImageData,
    },
    [bfcmLastChance]: {
      headerText: 'The Mother’s Day Sale Ends Soon!',
      headerColor: theme.space,
      subHeaderText: `Save on Mom's most wanted gifts`,
      subHeaderColor: theme.space,
      ctaText: 'Buy Now',
      ctaTextColor: theme.white,
      ctaTextColorMobile: theme.white,
      ctaBgColor: theme.coral,
      ctaLink: frame2 ? FRAME_2_BUY_LINK : frame ? BUY_LINK : CAL_BUY_LINK,
      labelColor: theme.space,
      timeColor: theme.space,
      bckgndDesk: bfcmVipBkgd.childImageSharp.gatsbyImageData,
      bckgndMobile: bfcmVipBkgdMobile.childImageSharp.gatsbyImageData,
    },
    [bau]: {
      headerText: 'LAST CHANCE TO SAVE!',
      headerColor: theme.space,
      subHeaderText: `Give the gift of lasting memories, for less.`,
      subHeaderColor: theme.space,
      ctaText: 'Buy Now',
      ctaTextColor: theme.white,
      ctaTextColorMobile: theme.white,
      ctaBgColor: theme.coral,
      ctaLink: frame2 ? FRAME_2_BUY_LINK : frame ? BUY_LINK : CAL_BUY_LINK,
      labelColor: theme.space,
      timeColor: theme.space,
      bckgndDesk: bfcmVipBkgd.childImageSharp.gatsbyImageData,
      bckgndMobile: bfcmVipBkgdMobile.childImageSharp.gatsbyImageData,
    },
  }

  const {
    headerText,
    subHeaderText,
    bckgndDesk,
    bckgndMobile,
    ctaText,
    ctaLink,
    headerColor,
    subHeaderColor,
    ctaTextColor,
    ctaTextColorMobile,
    ctaBgColor,
    labelColor,
    timeColor,
  } = holidayStates[holidayState] || holidayStates.bfcmCore

  return (
    <CountdownSection marginTop={marginTop} frame2={frame2} frame={frame && marginTop}>
      <HideOnMobile>
        <GatsbyImage className="countdown-background-image" image={bckgndDesk} alt="" />
      </HideOnMobile>
      <HideOnDesktop>
        <GatsbyImage className="countdown-background-image" image={bckgndMobile} alt="" />
      </HideOnDesktop>
      <CountdownSectionContainer>
        <CountDownContainer>
          <TextContainer>
            <Header headerColor={headerColor}>{t(headerText)}</Header>
          </TextContainer>
          <TimeContainer>
            {timeDisplay.map((time, i) => (
              <>
                <Col>
                  <Time timeColor={timeColor}>{time.content}</Time>
                  <Label labelColor={labelColor}>
                    <Trans>{time.label}</Trans>
                  </Label>
                </Col>
                {i < timeDisplay.length - 1 && (
                  <ColonWrapper>
                    <Time timeColor={timeColor}>:</Time>
                  </ColonWrapper>
                )}
              </>
            ))}
          </TimeContainer>
        </CountDownContainer>
        <div>
          <SubHeader
            subHeaderColor={subHeaderColor}
            dangerouslySetInnerHTML={{ __html: t(subHeaderText) }}
          />
          <BuyNowContainer>
            <BuyNowLink ctaBgColor={ctaBgColor} to={ctaLink}>
              <BuyNowText ctaTextColor={ctaTextColor} ctaTextColorMobile={ctaTextColorMobile}>
                {t(ctaText)}
              </BuyNowText>
            </BuyNowLink>
          </BuyNowContainer>
        </div>
      </CountdownSectionContainer>
    </CountdownSection>
  )
}

SeasonalCountDownTimer.propTypes = {
  holidayDate: PropTypes.string.isRequired,
  onCountdownComplete: PropTypes.func.isRequired,
  holidayState: PropTypes.oneOf(['vip', 'bfcmCore', 'bfcmLastChance', 'bau']).isRequired,
  marginTop: PropTypes.bool,
  frame: PropTypes.bool,
  frame2: PropTypes.bool,
}

export default SeasonalCountDownTimer
